import * as React from "react"
import * as DialogPrimitive from "@radix-ui/react-dialog"
import { X } from "lucide-react"
import { cn } from "../../lib/utils"

const DraggableDialog = DialogPrimitive.Root

const DraggableDialogTrigger = DialogPrimitive.Trigger

const DraggableDialogPortal = DialogPrimitive.Portal

const DraggableDialogClose = DialogPrimitive.Close

const DraggableDialogOverlay = React.forwardRef(({ className, ...props }, ref) => (
  <DialogPrimitive.Overlay
    ref={ref}
    className={cn(
      "fixed inset-0 z-50 bg-black/80 data-[state=open]:animate-in data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0",
      className
    )}
    {...props}
  />
))
DraggableDialogOverlay.displayName = DialogPrimitive.Overlay.displayName

// Dialog oben positioniert für mobile Geräte
const DraggableDialogContent = React.forwardRef(({ className, children, ...props }, ref) => {
  return (
    <DraggableDialogPortal>
      <DraggableDialogOverlay />
      <DialogPrimitive.Content
        ref={ref}
        className={cn(
          "fixed z-50 w-[95%] max-w-lg bg-background border rounded-lg shadow-lg",
          // Mobile: Oben positioniert mit etwas Abstand
          "left-[50%] translate-x-[-50%] top-4",
          // Max Höhe mit Scroll
          "max-h-[90vh] overflow-hidden flex flex-col",
          // Animationen
          "data-[state=open]:animate-in data-[state=closed]:animate-out",
          "data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0",
          "data-[state=closed]:zoom-out-95 data-[state=open]:zoom-in-95",
          "duration-200",
          className
        )}
        {...props}
      >
        {children}
        <DialogPrimitive.Close className="absolute right-4 top-4 rounded-sm opacity-70 ring-offset-background transition-opacity hover:opacity-100 focus:outline-none focus:ring-2 focus:ring-ring focus:ring-offset-2 disabled:pointer-events-none data-[state=open]:bg-accent data-[state=open]:text-muted-foreground z-10">
          <X className="h-4 w-4" />
          <span className="sr-only">Schliessen</span>
        </DialogPrimitive.Close>
      </DialogPrimitive.Content>
    </DraggableDialogPortal>
  )
})
DraggableDialogContent.displayName = DialogPrimitive.Content.displayName

const DraggableDialogHeader = ({
  className,
  ...props
}) => (
  <div
    className={cn(
      "flex flex-col space-y-1.5 text-center sm:text-left px-6 pt-6 pb-2 shrink-0",
      className
    )}
    {...props}
  />
)
DraggableDialogHeader.displayName = "DraggableDialogHeader"

// Scrollable body for dialog content
const DraggableDialogBody = ({
  className,
  ...props
}) => (
  <div
    className={cn(
      "flex-1 overflow-y-auto px-6 py-4",
      className
    )}
    {...props}
  />
)
DraggableDialogBody.displayName = "DraggableDialogBody"

const DraggableDialogFooter = ({
  className,
  ...props
}) => (
  <div
    className={cn(
      "flex flex-col-reverse sm:flex-row sm:justify-end sm:space-x-2 px-6 py-4 border-t border-border/50 bg-muted/30 shrink-0",
      className
    )}
    {...props}
  />
)
DraggableDialogFooter.displayName = "DraggableDialogFooter"

const DraggableDialogTitle = React.forwardRef(({ className, ...props }, ref) => (
  <DialogPrimitive.Title
    ref={ref}
    className={cn(
      "text-lg font-semibold leading-none tracking-tight",
      className
    )}
    {...props}
  />
))
DraggableDialogTitle.displayName = DialogPrimitive.Title.displayName

const DraggableDialogDescription = React.forwardRef(({ className, ...props }, ref) => (
  <DialogPrimitive.Description
    ref={ref}
    className={cn("text-sm text-muted-foreground", className)}
    {...props}
  />
))
DraggableDialogDescription.displayName = DialogPrimitive.Description.displayName

export {
  DraggableDialog,
  DraggableDialogPortal,
  DraggableDialogOverlay,
  DraggableDialogClose,
  DraggableDialogTrigger,
  DraggableDialogContent,
  DraggableDialogHeader,
  DraggableDialogBody,
  DraggableDialogFooter,
  DraggableDialogTitle,
  DraggableDialogDescription,
}
